import { useQuery } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '@/api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints';
import { useInstrument } from '@/api/instruments/use-instruments';

interface PriceChangeResult {
    change: number;
    changePercent: number;
    previousPrice: number;
    currentPrice: number;
}

interface ChartHistoryResponse {
    s: string; // 'ok' | 'error' | 'no_data'
    errmsg?: string;
    t?: number[]; // timestamps
    o?: number[]; // open prices
    h?: number[]; // high prices
    l?: number[]; // low prices
    c?: number[]; // close prices
    v?: number[]; // volumes
}

/**
 * Calculate price change percentage from current price and 24h ago price
 * @param instrumentId - The instrument ID
 * @param currentPrice - The current price
 * @param enabled - Whether to fetch chart history (default: false to avoid unnecessary API calls)
 */
export const usePriceChange = (
    instrumentId: string | undefined,
    currentPrice: number,
    enabled: boolean = false
) => {
    // Fetch instrument to get symbol
    const { data: instrument } = useInstrument(instrumentId || '');

    return useQuery<PriceChangeResult>({
        queryKey: ['price-change', instrumentId, currentPrice, instrument?.symbol],
        queryFn: async () => {
            if (!instrumentId || !currentPrice || !instrument?.symbol) {
                return {
                    change: 0,
                    changePercent: 0,
                    previousPrice: currentPrice,
                    currentPrice: currentPrice,
                };
            }

            try {
                // Get price from 24 hours ago
                const to = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
                const from = to - 24 * 60 * 60; // 24 hours ago

                const { data } = await httpClient.get<ChartHistoryResponse>(
                    API_ENDPOINTS.TRADER.CHART_HISTORY,
                    {
                        params: {
                            symbol: instrument.symbol,
                            resolution: '60', // 1 hour candles for 24h data
                            from: from,
                            to: to,
                        },
                    }
                );

                // Check if response is successful
                if (data.s !== 'ok' || !data.c || data.c.length === 0) {
                    // Return zero change if no data
                    return {
                        change: 0,
                        changePercent: 0,
                        previousPrice: currentPrice,
                        currentPrice: currentPrice,
                    };
                }

                // Get the oldest close price (24h ago) - close prices are in chronological order
                const previousPrice = data.c[0] || currentPrice;

                const change = currentPrice - previousPrice;
                const changePercent = previousPrice !== 0 ? (change / previousPrice) * 100 : 0;

                return {
                    change,
                    changePercent,
                    previousPrice,
                    currentPrice,
                };
            } catch (error) {
                console.error('Error fetching price history:', error);
                // Return zero change if API fails
                return {
                    change: 0,
                    changePercent: 0,
                    previousPrice: currentPrice,
                    currentPrice: currentPrice,
                };
            }
        },
        enabled: enabled && !!instrumentId && currentPrice > 0 && !!instrument?.symbol,
        refetchInterval: enabled ? 60000 : false, // Only refetch if enabled
        staleTime: 30000, // Consider data stale after 30 seconds
    });
};
